import { AccordionComponentContent } from '../../../types/elearning';
import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { sanitizeElearningHtml } from '../../../utils/elearningHtml';

interface AccordionViewerProps {
  content: AccordionComponentContent;
}

export default function AccordionViewer({ content }: AccordionViewerProps) {
  const { items } = content;
  const [expandedItems, setExpandedItems] = useState<Set<number>>(
    new Set(items.map((item, index) => (item.defaultExpanded ? index : -1)).filter(i => i !== -1))
  );

  const toggleItem = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  if (!items || items.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
        <p className="text-sm text-gray-500">No accordion items</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 mb-4">
      {items.map((item, index) => {
        const isExpanded = expandedItems.has(index);
        return (
          <div
            key={index}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            <button
              onClick={() => toggleItem(index)}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left transition-colors"
            >
              <span className="font-medium text-gray-900">{item.title}</span>
              {isExpanded ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
              )}
            </button>
            {isExpanded && (
              <div className="px-4 py-3 bg-white border-t border-gray-200">
                <div
                  className="text-gray-700 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeElearningHtml(item.content) }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
